import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Chip,
  IconButton,
  Switch,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import axios from 'axios';
import type { SoothRule, SoothRuleCreate } from '../../types/sooth';

const LEVEL_LABELS: Record<number, string> = {
  1: 'Sender Match',
  2: 'Keyword Match',
  3: 'Thread/Group Match',
  4: 'AI Fallback',
};

const LEVEL_COLORS: Record<number, 'primary' | 'secondary' | 'info' | 'warning'> = {
  1: 'primary',
  2: 'secondary',
  3: 'info',
  4: 'warning',
};

const emptyRule: SoothRuleCreate = {
  name: '',
  level: 2,
  order: 1,
  condition: { keywords: [] },
  action: 'notify',
  priority: 'medium',
  is_active: true,
};

const SoothRulesPage: React.FC = () => {
  const [rules, setRules] = useState<SoothRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<SoothRule | null>(null);
  const [formData, setFormData] = useState<SoothRuleCreate>(emptyRule);
  const [conditionText, setConditionText] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/v1/sooth/rules/');
      setRules(response.data.results || response.data);
      setError('');
    } catch (err) {
      setError('Failed to load rules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  const openCreate = () => {
    setEditingRule(null);
    setFormData(emptyRule);
    setConditionText('');
    setDialogOpen(true);
  };

  const openEdit = (rule: SoothRule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      level: rule.level,
      order: rule.order,
      condition: rule.condition,
      action: rule.action,
      priority: rule.priority,
      is_active: rule.is_active,
    });
    // Convert condition to user-friendly text
    if (rule.level === 1 && rule.condition.sender_ids) {
      setConditionText(rule.condition.sender_ids.join(', '));
    } else if (rule.level === 2 && rule.condition.keywords) {
      setConditionText(rule.condition.keywords.join(', '));
    } else if (rule.level === 3 && rule.condition.thread_ids) {
      setConditionText(rule.condition.thread_ids.join(', '));
    } else {
      setConditionText(JSON.stringify(rule.condition));
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Build condition from text based on level
      let condition: Record<string, any> = {};
      const items = conditionText.split(',').map((s) => s.trim()).filter(Boolean);
      if (formData.level === 1) {
        condition = { sender_ids: items };
      } else if (formData.level === 2) {
        condition = { keywords: items };
      } else if (formData.level === 3) {
        condition = { thread_ids: items };
      } else {
        condition = { prompt: conditionText };
      }

      const payload = { ...formData, condition };

      if (editingRule) {
        await axios.put(`/api/v1/sooth/rules/${editingRule.id}/`, payload);
      } else {
        await axios.post('/api/v1/sooth/rules/', payload);
      }
      setDialogOpen(false);
      fetchRules();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ruleId: number) => {
    if (!window.confirm('Delete this rule?')) return;
    try {
      await axios.delete(`/api/v1/sooth/rules/${ruleId}/`);
      fetchRules();
    } catch (err) {
      setError('Failed to delete rule');
    }
  };

  const handleToggle = async (rule: SoothRule) => {
    try {
      await axios.patch(`/api/v1/sooth/rules/${rule.id}/`, { is_active: !rule.is_active });
      fetchRules();
    } catch (err) {
      setError('Failed to toggle rule');
    }
  };

  const conditionLabel = (level: number): string => {
    switch (level) {
      case 1: return 'Phone numbers (comma-separated)';
      case 2: return 'Keywords (comma-separated, supports regex)';
      case 3: return 'Thread/Group IDs (comma-separated)';
      case 4: return 'AI prompt';
      default: return 'Condition';
    }
  };

  const formatCondition = (rule: SoothRule): string => {
    if (rule.level === 1 && rule.condition.sender_ids) {
      return rule.condition.sender_ids.join(', ') || '(none set)';
    }
    if (rule.level === 2 && rule.condition.keywords) {
      return rule.condition.keywords.join(', ');
    }
    if (rule.level === 3 && rule.condition.thread_ids) {
      return rule.condition.thread_ids.join(', ');
    }
    return JSON.stringify(rule.condition);
  };

  // Sort rules by level then order
  const sortedRules = [...rules].sort((a, b) => a.level - b.level || a.order - b.order);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h4">Classification Rules</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Add Rule
        </Button>
      </Stack>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Rules are evaluated in order: Sender Match → Keyword Match → Thread/Group Match → AI Fallback.
        First match wins.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : sortedRules.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary">No rules configured. Add a rule to start classifying messages.</Typography>
        </Paper>
      ) : (
        <Stack spacing={1}>
          {sortedRules.map((rule) => (
            <Paper
              key={rule.id}
              sx={{
                p: 2,
                opacity: rule.is_active ? 1 : 0.5,
                borderLeft: '4px solid',
                borderColor: rule.is_active ? `${LEVEL_COLORS[rule.level] || 'primary'}.main` : 'grey.400',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box sx={{ flex: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <Typography variant="subtitle1" fontWeight={600}>{rule.name}</Typography>
                    <Chip
                      label={LEVEL_LABELS[rule.level] || `Level ${rule.level}`}
                      size="small"
                      color={LEVEL_COLORS[rule.level] || 'default'}
                    />
                    <Chip label={rule.priority} size="small" variant="outlined" />
                    <Chip label={rule.action} size="small" variant="outlined" />
                  </Stack>
                  <Typography variant="body2" color="textSecondary">
                    {formatCondition(rule)}
                  </Typography>
                </Box>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Tooltip title={rule.is_active ? 'Active' : 'Inactive'}>
                    <Switch
                      checked={rule.is_active}
                      onChange={() => handleToggle(rule)}
                      size="small"
                    />
                  </Tooltip>
                  <IconButton size="small" onClick={() => openEdit(rule)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(rule.id)} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingRule ? 'Edit Rule' : 'Add Rule'}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Rule Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
            />
            <Stack direction="row" spacing={2}>
              <TextField
                select
                label="Level"
                value={formData.level}
                onChange={(e) => {
                  setFormData({ ...formData, level: Number(e.target.value) });
                  setConditionText('');
                }}
                sx={{ flex: 1 }}
              >
                <MenuItem value={1}>1 — Sender Match</MenuItem>
                <MenuItem value={2}>2 — Keyword Match</MenuItem>
                <MenuItem value={3}>3 — Thread/Group Match</MenuItem>
                <MenuItem value={4}>4 — AI Fallback</MenuItem>
              </TextField>
              <TextField
                label="Order"
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: Number(e.target.value) })}
                sx={{ width: 100 }}
              />
            </Stack>
            <TextField
              label={conditionLabel(formData.level)}
              value={conditionText}
              onChange={(e) => setConditionText(e.target.value)}
              multiline={formData.level === 4}
              rows={formData.level === 4 ? 3 : 1}
              fullWidth
              helperText={
                formData.level === 2
                  ? 'Supports regex patterns, e.g.: urgent|emergency, sick\\b'
                  : undefined
              }
            />
            <Stack direction="row" spacing={2}>
              <TextField
                select
                label="Action"
                value={formData.action}
                onChange={(e) => setFormData({ ...formData, action: e.target.value })}
                sx={{ flex: 1 }}
              >
                <MenuItem value="notify">Notify (real-time)</MenuItem>
                <MenuItem value="queue">Queue (check later)</MenuItem>
                <MenuItem value="summarise">Summarise (digest)</MenuItem>
                <MenuItem value="silence">Silence (ignore)</MenuItem>
              </TextField>
              <TextField
                select
                label="Priority"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                sx={{ flex: 1 }}
              >
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="none">None</MenuItem>
              </TextField>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !formData.name}
          >
            {saving ? 'Saving...' : editingRule ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SoothRulesPage;
